/**
 * @fileoverview API Error Alert Component for consistent error handling
 * @active NEW - Used across the application for error handling
 *
 * This component provides a standardized way to display API errors throughout the application.
 * It uses ChakraUI's Alert component with consistent styling and behavior.
 *
 * WHEN TO USE:
 * - When displaying API request failures
 * - When showing network connectivity issues
 * - When data is unavailable or incomplete
 * - To provide retry/recovery options to users
 * - As a replacement for inline error handling in individual components
 *
 * @see {@link src/services/apiService/axiosService.ts} - Main API service that categorizes errors
 * @see {@link src/app/global-error.tsx} - Global error handling for the application
 */

"use client";

import React from "react";
import { useRouter } from "next/navigation";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  Flex,
  AlertStatus,
} from "@chakra-ui/react";

/**
 * ApiErrorAlert component for displaying consistent API error states
 *
 * This is a reusable component for displaying API errors throughout the app.
 * It handles different error types with appropriate styling and actions.
 *
 * @example
 * // Simple usage with error object
 * <ApiErrorAlert error={error} />
 *
 * // With custom retry action
 * <ApiErrorAlert
 *   error={error}
 *   onRetry={() => refetchData()}
 *   showGoBack={false}
 * />
 */
interface ApiErrorAlertProps {
  /** Error object with type, message, and retry info */
  error?: {
    type?: string;
    message: string;
    canRetry?: boolean;
  };
  /** Custom title to display (defaults based on error type) */
  title?: string;
  /** Status for the alert component */
  status?: AlertStatus;
  /** Show a "Go Back" button */
  showGoBack?: boolean;
  /** Show a "Try Again" button */
  showRetry?: boolean;
  /** Whether to display as a full-page error */
  fullPage?: boolean;
  /** Custom retry handler */
  onRetry?: () => void;
  /** Classes to apply to the container */
  className?: string;
}

/**
 * Maps error types to user-friendly titles and alert statuses
 */
const ERROR_TYPE_MAP: Record<string, { title: string; status: AlertStatus }> = {
  NETWORK_ERROR: {
    title: "Connection Error",
    status: "error",
  },
  AUTH_ERROR: {
    title: "Authentication Error",
    status: "warning",
  },
  NOT_FOUND: {
    title: "Resource Not Found",
    status: "info",
  },
  SERVER_ERROR: {
    title: "Server Error",
    status: "error",
  },
  RATE_LIMIT: {
    title: "Too Many Requests",
    status: "warning",
  },
  UNKNOWN_ERROR: {
    title: "Data Unavailable",
    status: "error",
  },
  DATA_ERROR: {
    title: "Data May Be Incomplete",
    status: "warning",
  },
};

/**
 * ApiErrorAlert component
 */
export function ApiErrorAlert({
  error,
  title,
  status,
  showGoBack = true,
  showRetry = true,
  fullPage = false,
  onRetry,
  className,
}: ApiErrorAlertProps) {
  const router = useRouter();

  if (!error) return null;

  const errorType = error.type || "UNKNOWN_ERROR";
  const errorInfo = ERROR_TYPE_MAP[errorType] || ERROR_TYPE_MAP.UNKNOWN_ERROR;

  const alertTitle = title || errorInfo.title;
  const alertStatus = status || errorInfo.status;
  const canRetry = error.canRetry !== undefined ? error.canRetry : true;

  // Handle retry action
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      router.refresh();
    }
  };

  // Adjust styles based on fullPage prop
  const alertStyles = fullPage
    ? {
        flexDirection: "column" as const,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        textAlign: "center" as const,
        height: "200px",
        mt: "6",
      }
    : {};

  return (
    <Alert
      status={alertStatus}
      variant="subtle"
      borderRadius="md"
      mt={4}
      mb={4}
      className={className}
      {...alertStyles}
    >
      <AlertIcon boxSize={fullPage ? "40px" : "24px"} mr={fullPage ? 0 : 3} />

      <Box w="100%">
        <AlertTitle
          mt={fullPage ? 4 : 0}
          mb={fullPage ? 1 : 0}
          fontSize={fullPage ? "lg" : "md"}
        >
          {alertTitle}
        </AlertTitle>

        <AlertDescription
          display="block"
          maxWidth={fullPage ? "sm" : "auto"}
          mb={fullPage ? 4 : 0}
        >
          {error.message}
        </AlertDescription>

        {(showRetry || showGoBack) && (
          <Flex
            mt={2}
            gap={4}
            justifyContent={fullPage ? "center" : "flex-start"}
            flexWrap="wrap"
          >
            {showRetry && canRetry && (
              <Button
                size={fullPage ? "md" : "sm"}
                colorScheme="blue"
                onClick={handleRetry}
              >
                Try Again
              </Button>
            )}

            {showGoBack && (
              <Button
                size={fullPage ? "md" : "sm"}
                variant="outline"
                onClick={() => router.back()}
              >
                Go Back
              </Button>
            )}
          </Flex>
        )}
      </Box>
    </Alert>
  );
}

export default ApiErrorAlert;
